import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { TLanguage } from '@lib/core/retailers/types';
import { isAppInIframe } from '@lib/core/service/utils';
import { localeApp } from '@lib/tools/locale/source/web/app';
import { localeCommon } from '@lib/tools/locale/source/web/common';

import { ReactComponent as HamburgerMenuCloseIcon } from '@components/web/src/assets/icons/hamburger/hamburger-menu-close.svg';
import { ReactComponent as HamburgerMenuOpenIcon } from '@components/web/src/assets/icons/hamburger/hamburger-menu-open.svg';
import { ReactComponent as ExitIcon } from '@components/web/src/assets/icons/hamburger/header-exit-icon.svg';
import { ReactComponent as LanguageIcon } from '@components/web/src/assets/icons/hamburger/header-language-icon.svg';
import { ReactComponent as SettingsIcon } from '@components/web/src/assets/icons/hamburger/header-settings-icon.svg';
import { HeaderDropDownMenu } from '@components/web/src/atoms/Headers/HeaderWidget/HeaderDropDownMenu/HeaderDropDownMenu';
import * as S from '@components/web/src/atoms/Headers/HeaderWidget/styles';
import { VinhoodLogo } from '@components/web/src/atoms/VinhoodLogo/VinhoodLogo';
import { Image } from '@components/web/src/foundations/Image/Image';
import { LanguageSwitcher } from '@components/web/src/organisms/LanguageSwitcher/LanguageSwitcher';

interface Props {
  shouldShowHeaderDropDownMenu: boolean;
  producerName?: string;
  isEnableExitNavigation: boolean;
  isEnableSettingsNavigation: boolean;
  isEnableLanguageSwitcher: boolean;
  isShowLanguageSwitcher: boolean;
  retailerLanguages: TLanguage[];
  locale: string;
  customHeaderLogo?: string;
  handleLogoClick: () => void;
  handleSettingPageNavigation: () => void;
  handleSelectLanguage: (selectedLanguageCode: string) => void;
}

export const HeaderWidget = ({
  shouldShowHeaderDropDownMenu,
  retailerLanguages,
  locale,
  isEnableExitNavigation,
  isEnableSettingsNavigation,
  isEnableLanguageSwitcher,
  producerName,
  isShowLanguageSwitcher,
  customHeaderLogo,
  handleLogoClick,
  handleSettingPageNavigation,
  handleSelectLanguage,
}: Props) => {
  const { exitPartnerNavigation, switchToLanguage } = localeCommon?.headerDropDownMenu;
  const { settingsTitle } = localeApp?.settings;

  const { pathname } = useLocation();

  const [isMenuOpened, setIsMenuOpened] = useState(false);

  useEffect(() => {
    setIsMenuOpened(false);
  }, [pathname]);

  const handleSelectLanguageOptionClick = (selectedLanguageCode: string) => {
    setIsMenuOpened(false);
    handleSelectLanguage(selectedLanguageCode);
  };

  const handleExitOptionClick = () => setIsMenuOpened(false);

  const handleSettingsOptionClick = () => {
    setIsMenuOpened(false);
    handleSettingPageNavigation();
  };

  const menuOptions = useMemo(() => {
    const switcherLanguages = retailerLanguages
      .filter(({ code }) => code !== locale)
      .map(({ code, language }) => ({
        icon: <LanguageIcon />,
        localeIndex: { language },
        localeOptions: { language },
        onClick: () => handleSelectLanguageOptionClick(code),
        text: switchToLanguage,
      }));

    return [
      isEnableSettingsNavigation && {
        icon: <SettingsIcon />,
        onClick: handleSettingsOptionClick,
        text: settingsTitle,
      },
      ...(isEnableLanguageSwitcher ? switcherLanguages : []),
      isEnableExitNavigation && {
        icon: <ExitIcon />,
        localeIndex: { producerName },
        localeOptions: { producerName },
        onClick: handleExitOptionClick,
        text: exitPartnerNavigation,
      },
    ].filter(Boolean);
  }, [
    retailerLanguages,
    locale,
    isEnableSettingsNavigation,
    isEnableLanguageSwitcher,
    isEnableExitNavigation,
    producerName,
  ]);

  const headerLogo = customHeaderLogo ? <Image alt="logo" src={customHeaderLogo} /> : <VinhoodLogo />;

  const menuIcon = isMenuOpened ? <HamburgerMenuCloseIcon /> : <HamburgerMenuOpenIcon />;

  return (
    <S.HeaderWidgetContainer
      isFullWidth
      $isAppInIframe={isAppInIframe}
      $isCenteredLogo={!shouldShowHeaderDropDownMenu && !isShowLanguageSwitcher}
    >
      <S.LogoWrapper onClick={handleLogoClick}>{headerLogo}</S.LogoWrapper>
      {isShowLanguageSwitcher && !shouldShowHeaderDropDownMenu && (
        <LanguageSwitcher languageSwitcherType="toggle" locale={locale} retailerLanguages={retailerLanguages} />
      )}
      {shouldShowHeaderDropDownMenu && (
        <>
          <S.MenuIconWrapper align="center" justify="center" onClick={() => setIsMenuOpened(prevState => !prevState)}>
            {menuIcon}
          </S.MenuIconWrapper>

          {isMenuOpened && (
            <HeaderDropDownMenu isMenuOpened={isMenuOpened} menuOptions={menuOptions} producerName={producerName} />
          )}
        </>
      )}
    </S.HeaderWidgetContainer>
  );
};
